import type { FileStatusId } from '@steerfi/shared';
import { FILE_STATUS, isInternalAdminUser, isInternalUser } from '@steerfi/shared';
import { CheckCircle, CircleStop, CircleX } from 'lucide-react';
import type React from 'react';
import { useState } from 'react';
import { toast } from 'sonner';
import { createDisbursements } from '@/api/disbursements';
import { type PaymentFileItem, processFile as processFileApi } from '@/api/file-upload-api';
import { updatePaymentFileStatus } from '@/api/payments';
import { useCurrentUser } from '@/hooks/useCurrentUser';

export interface PaymentFileActionButton {
  label: string;
  loadingLabel: string;
  onClick: () => void;
  icon: React.ElementType;
  variant?: 'default' | 'destructive' | 'secondary' | 'outline';
}

interface UsePaymentFileActionsOptions {
  paymentFile: PaymentFileItem | null;
  /** Called after a status action succeeds so the caller can refetch. */
  onActionSuccess?: () => void;
}

interface UsePaymentFileActionsResult {
  isProcessing: boolean;
  actionButtons: PaymentFileActionButton[];
  showDownloadButton: boolean;
}

const DOWNLOADABLE_STATUSES: FileStatusId[] = [
  FILE_STATUS.PROCESSED,
  FILE_STATUS.COMPLETED,
  FILE_STATUS.FUNDS_RECEIVED,
  FILE_STATUS.RELEASED,
  FILE_STATUS.READY_FOR_RELEASE,
  FILE_STATUS.AWAITING_FUNDS,
  FILE_STATUS.WAITING_FOR_APPROVAL,
];

/**
 * Status-dependent payment-file actions (process/reject, approve/disapprove, funds received,
 * re-process) plus whether the download action should be shown. Shared by the detail subpage.
 */
export function usePaymentFileActions({
  paymentFile,
  onActionSuccess,
}: UsePaymentFileActionsOptions): UsePaymentFileActionsResult {
  const currentUser = useCurrentUser();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleProcessFile = async () => {
    if (!paymentFile) return;
    setIsProcessing(true);
    try {
      const result = await processFileApi(paymentFile.file_id);
      const pr = result.processing_result;
      const successCount = pr?.processed_successfully ?? pr?.total_rows ?? 0;
      const totalCount = pr?.total_rows ?? 0;
      const failedCount = totalCount - successCount;

      toast.success('Payment file processed successfully', {
        description:
          failedCount > 0
            ? `${successCount} of ${totalCount} rows processed. ${failedCount} row(s) failed.`
            : `Payments have been created from the file.`,
      });
      onActionSuccess?.();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to process payment file';
      toast.error('Processing failed', { description: errorMessage });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleApprovePayments = async () => {
    if (!paymentFile) return;
    setIsProcessing(true);
    try {
      await updatePaymentFileStatus(paymentFile.file_id, FILE_STATUS.AWAITING_FUNDS);
      toast.success('Payment File has been approved', {
        description: 'All payments in the file are approved for release.',
      });
      onActionSuccess?.();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to approve payment file';
      toast.error('Processing failed', { description: errorMessage });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDisapprovePayments = async () => {
    if (!paymentFile) return;
    setIsProcessing(true);
    try {
      await updatePaymentFileStatus(paymentFile.file_id, FILE_STATUS.DISAPPROVED);
      toast.warning('Payment File has been disapproved', {
        description: 'All payments in the file have been disapproved.',
      });
      onActionSuccess?.();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to disapprove payment file';
      toast.error('Processing failed', { description: errorMessage });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFundsReceived = async () => {
    if (!paymentFile) return;
    setIsProcessing(true);
    try {
      await updatePaymentFileStatus(paymentFile.file_id, FILE_STATUS.FUNDS_RECEIVED);

      try {
        const disbursementResult = await createDisbursements(paymentFile.file_id);
        if (disbursementResult.success) {
          toast.success('Funds Received & Disbursement Created', {
            description:
              'All payments in the file are funded and disbursement file has been created.',
          });
        } else {
          toast.warning('Funds Received', {
            description: 'Funds received status updated, but disbursement creation had issues.',
          });
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to create disbursement';
        toast.warning('Funds Received', {
          description: `Funds received status updated, but disbursement creation failed: ${errorMessage}`,
        });
      }

      onActionSuccess?.();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to update funds received status';
      toast.error('Processing failed', { description: errorMessage });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRejectFile = async () => {
    if (!paymentFile) return;
    setIsProcessing(true);
    try {
      await updatePaymentFileStatus(paymentFile.file_id, FILE_STATUS.REJECTED);
      toast.warning('Payment File has been rejected', {
        description: 'All payments in the file have been rejected.',
      });
      onActionSuccess?.();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to reject payment file';
      toast.error('Processing failed', { description: errorMessage });
    } finally {
      setIsProcessing(false);
    }
  };

  const actionButtons: PaymentFileActionButton[] = paymentFile
    ? (() => {
        const status = paymentFile.status_type_id as FileStatusId;
        const internal = isInternalUser(currentUser?.roles);
        // Money movement (confirming funds) is an admin-tier action.
        const internalAdmin = isInternalAdminUser(currentUser?.roles);

        if (([FILE_STATUS.RECEIVED, FILE_STATUS.PENDING] as FileStatusId[]).includes(status)) {
          return [
            {
              label: 'Reject',
              loadingLabel: 'Rejecting...',
              onClick: handleRejectFile,
              icon: CircleX,
              variant: 'destructive',
            },
            {
              label: 'Process File',
              loadingLabel: 'Processing...',
              onClick: handleProcessFile,
              icon: CheckCircle,
              variant: 'outline',
            },
          ];
        }
        if (
          !internal &&
          ([FILE_STATUS.WAITING_FOR_APPROVAL, FILE_STATUS.PROCESSED] as FileStatusId[]).includes(
            status
          )
        ) {
          return [
            {
              label: 'Approve File',
              loadingLabel: 'Approving...',
              onClick: handleApprovePayments,
              icon: CheckCircle,
            },
            {
              label: 'Disapprove File',
              loadingLabel: 'Disapproving...',
              onClick: handleDisapprovePayments,
              icon: CircleStop,
              variant: 'destructive',
            },
          ];
        }
        if (
          internalAdmin &&
          ([FILE_STATUS.AWAITING_FUNDS, FILE_STATUS.APPROVED] as FileStatusId[]).includes(status)
        ) {
          return [
            {
              label: 'Funds Received',
              loadingLabel: 'Confirming...',
              onClick: handleFundsReceived,
              icon: CheckCircle,
            },
          ];
        }
        if (
          ([FILE_STATUS.FAILED, FILE_STATUS.NEEDS_REVIEW] as FileStatusId[]).includes(status) &&
          (internal || currentUser?.partyId === paymentFile.party_id)
        ) {
          return [
            {
              label: 'Re-process File',
              loadingLabel: 'Re-processing...',
              onClick: handleProcessFile,
              icon: CheckCircle,
              variant: 'outline',
            },
          ];
        }
        return [];
      })()
    : [];

  const showDownloadButton = paymentFile
    ? DOWNLOADABLE_STATUSES.includes(paymentFile.status_type_id as FileStatusId)
    : false;

  return { isProcessing, actionButtons, showDownloadButton };
}
